import type {
  CriticalWorkflowInitiatingEvent,
  CriticalWorkflowPerformanceMetrics,
} from './types';

interface LongAnimationFrameEntry extends PerformanceEntry {
  blockingDuration: number;
}

interface EventTimingEntry extends PerformanceEntry {
  processingStart: number;
  processingEnd: number;
  interactionId: number;
}

export interface RecordedEventTiming {
  name: string;
  startTime: number;
  duration: number;
  processingStart: number;
  processingEnd: number;
  interactionId: number;
}

export const isLongAnimationFrameEntry = (
  entry: PerformanceEntry
): entry is LongAnimationFrameEntry =>
  entry.entryType === 'long-animation-frame' &&
  'blockingDuration' in entry &&
  typeof entry.blockingDuration === 'number';

export const isEventTimingEntry = (
  entry: PerformanceEntry
): entry is EventTimingEntry =>
  entry.entryType === 'event' &&
  'processingStart' in entry &&
  typeof entry.processingStart === 'number' &&
  'processingEnd' in entry &&
  typeof entry.processingEnd === 'number' &&
  'interactionId' in entry &&
  typeof entry.interactionId === 'number';

export const getMedian = (values: number[]): number | undefined => {
  if (values.length === 0) {
    return undefined;
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);
  if (sortedValues.length % 2 === 1) {
    return sortedValues[middleIndex];
  }
  return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
};

export const getInteractionMetrics = (
  recentEventTimings: readonly RecordedEventTiming[],
  initiatingEvent: CriticalWorkflowInitiatingEvent | undefined
): Partial<CriticalWorkflowPerformanceMetrics> => {
  if (initiatingEvent == null) {
    return {};
  }

  const initiatingEventTiming = recentEventTimings.find(
    entry =>
      entry.name === initiatingEvent.name &&
      entry.startTime === initiatingEvent.startTime
  );
  if (initiatingEventTiming == null) {
    return {};
  }

  const interactionEntries =
    initiatingEventTiming.interactionId === 0
      ? [initiatingEventTiming]
      : recentEventTimings.filter(
          entry => entry.interactionId === initiatingEventTiming.interactionId
        );
  const slowestEntry = interactionEntries.reduce((slowest, entry) =>
    entry.duration > slowest.duration ? entry : slowest
  );

  return {
    interactionDurationMs: slowestEntry.duration,
    inputDelayMs: slowestEntry.processingStart - slowestEntry.startTime,
    processingDurationMs:
      slowestEntry.processingEnd - slowestEntry.processingStart,
    presentationDelayMs: Math.max(
      0,
      slowestEntry.startTime +
        slowestEntry.duration -
        slowestEntry.processingEnd
    ),
    interactionId: initiatingEventTiming.interactionId,
  };
};
